// Parent-side controller untuk sandbox iframe.

import {
  MSG_INIT,
  MSG_STOP,
  MSG_DESTROY,
  MSG_SANDBOX_LOADED,
  MSG_READY,
  MSG_ERROR,
  MSG_BOOT_ERROR,
  MSG_PERF,
  MSG_CODE_EXECUTED,
  MSG_STOPPED,
  MSG_DESTROYED,
  MSG_ASSET_LOADED,
  makeMessage,
  parseMessage,
} from './protocol.js'
import { loadPlaceholderAssets } from './assets.js'
import { showError, hideError } from './ErrorOverlay.js'

const IFRAME_SRC = '/sandbox/iframe.html'

let iframeEl = null
let messageHandler = null
let state = 'idle' // 'idle' | 'booting' | 'ready' | 'running' | 'stopped' | 'error' | 'destroyed'
let pendingInit = null

export function mountSandboxIframe(container) {
  if (iframeEl) {
    if (iframeEl.parentElement !== container) {
      container.appendChild(iframeEl)
    }
    return iframeEl
  }

  iframeEl = document.createElement('iframe')
  iframeEl.src = IFRAME_SRC
  // allow-scripts: jalankan JS.
  // allow-same-origin: agar origin iframe = origin parent, supaya
  // `import()` dari blob: URL hasil URL.createObjectURL(blob) di dalam
  // iframe bisa di-fetch (blob:null/... ditolak oleh browser kalau origin
  // iframe opaque). Isolasi tetap dijaga oleh CSP iframe.html + postMessage
  // sebagai satu-satunya channel keluar.
  iframeEl.setAttribute('sandbox', 'allow-scripts allow-same-origin')
  iframeEl.setAttribute('title', 'Studio Sandbox')
  iframeEl.setAttribute('aria-label', 'Area main game')
  iframeEl.className = 'tm-sandbox-iframe'
  iframeEl.style.cssText = 'width:100%;height:100%;border:0;display:block;background:#000;'
  iframeEl.style.visibility = 'hidden'

  container.appendChild(iframeEl)

  messageHandler = (e) => {
    if (e.source !== iframeEl.contentWindow) return
    const msg = parseMessage(e.data)
    if (!msg) return
    handleMessage(msg)
  }
  window.addEventListener('message', messageHandler)

  return iframeEl
}

export function prewarmSandbox() {
  if (iframeEl) return
  const tempContainer = document.createElement('div')
  tempContainer.style.cssText =
    'position:absolute;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;'
  document.body.appendChild(tempContainer)
  mountSandboxIframe(tempContainer)
}

async function handleMessage(msg) {
  const { type, payload } = msg
  switch (type) {
    case MSG_SANDBOX_LOADED:
      state = 'ready'
      if (pendingInit) {
        const init = pendingInit
        pendingInit = null
        await doInit(init.userCode, init.initialSprites, init.options || {})
      }
      break

    case MSG_READY:
      state = 'running'
      hideError()
      break

    case MSG_CODE_EXECUTED:
      break

    case MSG_ASSET_LOADED:
      break

    case MSG_PERF:
      break

    case MSG_BOOT_ERROR:
    case MSG_ERROR:
      state = 'error'
      showError(payload, containerEl())
      break

    case MSG_STOPPED:
      state = 'stopped'
      break

    case MSG_DESTROYED:
      state = 'destroyed'
      break
  }
}

function containerEl() {
  return iframeEl?.parentElement
}

async function resolvePhaserChunkUrl() {
  if (import.meta.env.DEV) {
    return '/src/sandbox/phaser-entry.js'
  }
  try {
    const res = await fetch('/.vite/manifest.json')
    if (res.ok) {
      const manifest = await res.json()
      for (const [key, entry] of Object.entries(manifest)) {
        if (entry.name === 'phaser' || key.includes('phaser-entry')) {
          return `/${entry.file}`
        }
      }
    }
  } catch {
    // ignore manifest errors
  }
  return '/assets/phaser.js'
}

async function doInit(userCode, initialSprites = [], options = {}) {
  try {
    const assets = await loadPlaceholderAssets()
    const phaserChunkUrl = await resolvePhaserChunkUrl()

    iframeEl.contentWindow.postMessage(
      makeMessage(MSG_INIT, { assets, userCode, initialSprites, phaserChunkUrl, options }),
      '*',
    )
    iframeEl.style.visibility = 'visible'
    state = 'booting'
  } catch (err) {
    showError(
      {
        message: `Gagal prepare game: ${err.message}`,
        friendlyMessage: 'Hmm, ada masalah menyiapkan game.',
      },
      containerEl(),
    )
  }
}

export async function initSandbox(userCode, initialSprites, options = {}) {
  if (!iframeEl) {
    throw new Error('Sandbox belum di-mount. Panggil mountSandboxIframe dulu.')
  }
  if (state === 'idle' || state === 'destroyed') {
    pendingInit = { userCode, initialSprites, options }
    return
  }
  await doInit(userCode, initialSprites, options)
}

export function stopSandbox() {
  if (!iframeEl || state === 'idle' || state === 'destroyed') return
  iframeEl.contentWindow.postMessage(makeMessage(MSG_STOP), '*')
}

export function destroySandbox() {
  if (!iframeEl) return
  if (state !== 'destroyed' && state !== 'idle') {
    iframeEl.contentWindow.postMessage(makeMessage(MSG_DESTROY), '*')
  }
  setTimeout(() => {
    unmountSandbox()
  }, 500)
}

export function unmountSandbox() {
  if (messageHandler) {
    window.removeEventListener('message', messageHandler)
    messageHandler = null
  }
  if (iframeEl) {
    iframeEl.remove()
    iframeEl = null
  }
  state = 'idle'
  pendingInit = null
}

export function getSandboxState() {
  return state
}
